import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, Image as ImageIcon, Lightbulb, MessageSquareReply } from "lucide-react";
import { useEffect } from "react";

import { api } from "../api/client";
import type { FeedbackItem, FeedbackMine } from "../api/types";
import { Callout } from "../components/Callout";
import { Dialog } from "../components/Dialog";
import { EmptyState } from "../components/EmptyState";
import { EXIT_MS } from "../components/presence";
import { Badge, Button } from "../components/ui";
import { t } from "../i18n/i18n";
import { formatDateTime } from "../licence/licence";
import { FEEDBACK_KEY, KIND_LABELS, openFeedback, SEVERITY_LABELS, STATUS_LABELS, STATUS_TONES } from "./feedbackModel";

/** État d'un retour, en pastille. */
export function StatusBadge({ item }: { item: Pick<FeedbackItem, "status"> }) {
  return (
    <Badge tone={STATUS_TONES[item.status]} dot>
      {t(STATUS_LABELS[item.status])}
    </Badge>
  );
}

/** Icône du type d'un retour. */
export function KindIcon({ item, size = 14 }: { item: Pick<FeedbackItem, "kind" | "severity">; size?: number }) {
  return item.kind === "anomaly" ? (
    <Bug size={size} aria-hidden className={item.severity === "blocking" ? "text-danger" : "text-warn"} />
  ) : (
    <Lightbulb size={size} aria-hidden className="text-accent" />
  );
}

/** « Mes retours » (registre F39) : les retours envoyés depuis cette licence (ce compte, en mode comptes), leur
 *  état et la réponse de l'équipe ; les réponses non lues sont signalées puis marquées lues à l'ouverture. */
export function MyFeedbackDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const mine = useQuery<FeedbackMine>({ queryKey: FEEDBACK_KEY, queryFn: api.feedback.mine, staleTime: 0 });
  const unread = mine.data?.unread ?? 0;
  useEffect(() => {
    if (!unread) return;
    // Réponses montrées : lues (la pastille « Nouvelle réponse » reste jusqu'à la fermeture).
    void api.feedback.seen().catch(() => undefined);
  }, [unread]);
  const close = () => {
    void queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
    onClose();
  };
  // Nouveau retour : cette fenêtre sort d'abord, la capture montre la vue de travail et non « Mes retours ».
  const write = (kind: "anomaly" | "evolution") => {
    close();
    window.setTimeout(() => openFeedback(kind, queryClient), EXIT_MS + 60);
  };
  const items = mine.data?.items ?? [];
  return (
    <Dialog
      title={t("Mes retours")}
      subtitle={t("Évolutions proposées et anomalies signalées depuis cette licence, avec la réponse de l'équipe Generex.")}
      onClose={close}
      width={640}
      footer={
        <>
          <Button variant="secondary" onClick={() => write("anomaly")}>
            {t("Signaler une anomalie")}
          </Button>
          <Button variant="secondary" onClick={() => write("evolution")}>
            {t("Suggérer une évolution")}
          </Button>
          <Button onClick={close}>{t("Fermer")}</Button>
        </>
      }
    >
      {mine.isError ? (
        <div className="p-4">
          <Callout tone="danger">{t("Vos retours n'ont pas pu être lus : réessayez dans un instant.")}</Callout>
        </div>
      ) : !mine.data ? (
        <EmptyState variant="loading" title={t("Chargement…")} compact />
      ) : items.length === 0 ? (
        <EmptyState icon={MessageSquareReply} title={t("Aucun retour envoyé")} compact>
          {t("Une idée d'évolution, une anomalie ? Elles arrivent ici avec la réponse de l'équipe.")}
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((item) => (
            <li key={item.id} className="space-y-1.5 px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex shrink-0">
                  <KindIcon item={item} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ui leading-snug font-medium break-words text-ink">{item.title}</p>
                  <p className="text-meta leading-snug text-ink-muted">
                    {t("N° {n}", { n: item.number })} · {t(KIND_LABELS[item.kind])}
                    {item.severity ? ` · ${t(SEVERITY_LABELS[item.severity])}` : ""} · {formatDateTime(item.created_at)}
                    {item.author ? ` · ${item.author}` : ""}
                  </p>
                </div>
                {item.has_screenshot && (
                  <a
                    href={api.feedback.screenshotUrl(item.id)}
                    target="_blank"
                    rel="noreferrer"
                    title={t("Voir la capture jointe")}
                    aria-label={t("Voir la capture jointe")}
                    className="mt-0.5 flex shrink-0 text-ink-muted hover:text-ink"
                  >
                    <ImageIcon size={14} />
                  </a>
                )}
                <StatusBadge item={item} />
              </div>
              {item.reply && (
                <div className="ml-6 rounded-lg bg-canvas px-3 py-2">
                  <p className="flex items-center gap-2 text-meta font-medium text-ink-soft">
                    {t("Réponse de l'équipe")} · {formatDateTime(item.replied_at)}
                    {item.unread && (
                      <Badge tone="accent" size="sm">
                        {t("Nouvelle réponse")}
                      </Badge>
                    )}
                  </p>
                  <p className="mt-1 text-ui leading-relaxed whitespace-pre-wrap text-ink">{item.reply}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
